/**
 * "Accept and commit" confirmation for step 2 (f10.5, design D10 section 7):
 * the values that will be stored, the aliases that will be recorded and the
 * later steps that become stale. Every number is the server's `commit_plan`.
 */

import { Check, Info } from 'lucide-react';

import type { BillableDaysReview } from '@/api/client';
import { Modal } from '@/routes/reference-data/Modal';

import { refusalMessage } from './billableDaysView';

export function BillableDaysCommitDialog({
  review,
  busy,
  error,
  onClose,
  onConfirm,
}: {
  review: BillableDaysReview;
  busy: boolean;
  error: unknown;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const plan = review.commit_plan;
  const stale = plan.later_steps_becoming_stale;
  return (
    <Modal
      title="Accept and commit?"
      subtitle={`Billable days · ${review.batch.original_filename ?? `batch #${review.batch.batch_number}`}`}
      onClose={onClose}
      onSubmit={onConfirm}
      busy={busy}
      submitLabel="Accept and commit"
      submitIcon={<Check aria-hidden="true" />}
      layout="plain"
      message={refusalMessage(error)}
    >
      <dl className="dl cols-2 mb-4">
        <div>
          <dt>Values stored</dt>
          <dd>{plan.values_to_commit.toLocaleString()}</dd>
        </div>
        <div>
          <dt>Step after commit</dt>
          <dd>Complete</dd>
        </div>
        <div>
          <dt>Aliases recorded</dt>
          <dd>
            {plan.aliases_to_add.length
              ? plan.aliases_to_add.map((alias) => `${alias.alias} → ${alias.country_code}`).join(', ')
              : 'None'}
          </dd>
        </div>
        <div>
          <dt>Later completed steps become stale</dt>
          <dd>{stale.length ? stale.join(', ') : 'None'}</dd>
        </div>
      </dl>
      <div className="alert alert-info">
        <Info aria-hidden="true" />
        <div className="a-body">
          The commit runs as a background job and stores everything in one transaction &mdash; all of it, or
          nothing. Excluded columns store nothing.
        </div>
      </div>
    </Modal>
  );
}
